import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { ASSETS } from "@/content/assets";
import { MAIN_NAV, JOURNEY_MENU } from "@/content/site";

const desktopLink =
  "text-[15px] font-medium text-ink/80 transition-colors duration-200 hover:text-pine";
const desktopActive = "text-pine underline decoration-brass decoration-2 underline-offset-8";

function MenuItems({ onNavigate }) {
  return JOURNEY_MENU.map((item, i) =>
    item.type === "label" ? (
      <p key={i} className="eyebrow px-5 pb-1 pt-4 text-[10px]">
        {item.label}
      </p>
    ) : (
      <Link
        key={i}
        to={item.to}
        onClick={onNavigate}
        className="block px-5 py-2 text-[15px] text-ink/85 transition-colors duration-150 hover:bg-canvas hover:text-pine"
      >
        {item.label}
      </Link>
    ),
  );
}

export default function Header() {
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileJourneys, setMobileJourneys] = useState(false);
  const dropRef = useRef(null);
  const dropButtonRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setDropOpen(false);
    setMobileOpen(false);
    setMobileJourneys(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const onDropKeyDown = (e) => {
    if (e.key === "Escape") {
      setDropOpen(false);
      dropButtonRef.current?.focus();
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-canvas/90 backdrop-blur-md">
      <div className="container-x flex h-20 items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center" aria-label="Wandering Glider Experiences, home">
          <img src={ASSETS.logo.src} alt={ASSETS.logo.alt} className="h-14 w-auto" />
        </Link>

        <nav aria-label="Main navigation" className="hidden items-center gap-8 lg:flex">
          {MAIN_NAV.map((item) =>
            item.label === "Journeys" ? (
              <div key={item.label} className="relative" ref={dropRef}>
                <button
                  ref={dropButtonRef}
                  type="button"
                  aria-expanded={dropOpen}
                  aria-haspopup="true"
                  onClick={() => setDropOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !dropOpen) {
                      e.preventDefault();
                      setDropOpen(true);
                    }
                  }}
                  data-testid="journeys-dropdown-button"
                  className={`${desktopLink} inline-flex items-center gap-1.5 ${
                    location.pathname.startsWith("/journeys") ? desktopActive : ""
                  }`}
                >
                  Journeys
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {dropOpen && (
                  <div
                    className="absolute left-1/2 top-full w-72 -translate-x-1/2 pt-4"
                    onKeyDown={onDropKeyDown}
                  >
                    <div className="border border-line bg-white py-2 shadow-[0_8px_24px_rgba(17,17,17,0.07)]">
                      <MenuItems onNavigate={() => setDropOpen(false)} />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `${desktopLink} ${isActive ? desktopActive : ""}`}
              >
                {item.label}
              </NavLink>
            ),
          )}
          <Link
            to="/start-your-journey"
            data-testid="nav-start-your-journey-cta"
            className="bg-pine px-5 py-2.5 text-[15px] font-medium text-canvas transition-colors duration-200 hover:bg-pinedeep"
          >
            Start Your Journey
          </Link>
        </nav>

        <button
          type="button"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-button"
          className="inline-flex h-11 w-11 items-center justify-center text-ink lg:hidden"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <nav
          id="mobile-menu"
          aria-label="Mobile navigation"
          className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-line bg-canvas lg:hidden"
        >
          <div className="container-x flex flex-col py-6">
            {MAIN_NAV.map((item) =>
              item.label === "Journeys" ? (
                <div key={item.label} className="border-b border-line">
                  <button
                    type="button"
                    aria-expanded={mobileJourneys}
                    onClick={() => setMobileJourneys((v) => !v)}
                    data-testid="mobile-journeys-toggle"
                    className="flex w-full items-center justify-between py-4 text-left text-lg font-medium text-ink"
                  >
                    Journeys
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${mobileJourneys ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  {mobileJourneys && (
                    <div className="pb-4 pl-4">
                      <MenuItems onNavigate={() => setMobileOpen(false)} />
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-line py-4 text-lg font-medium text-ink"
                >
                  {item.label}
                </NavLink>
              ),
            )}
            <Link
              to="/start-your-journey"
              data-testid="mobile-nav-start-your-journey-cta"
              className="mt-6 bg-pine px-6 py-4 text-center text-base font-medium text-canvas"
            >
              Start Your Journey
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
